// TopDatesTable.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
    Box,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";

import HoverPaper from "../HoverPaper";
import NoDataRow from "../../NoDataRow";

import { fns } from "../../../helpers/common";
import {
    DATATYPE_RESDATE,
    DISPLAY_ELEMENT_HELPER,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    getMetricName,
} from "../../../helpers/filters";
import InfoIcon from "../../InfoIcon";

const TOP_OPTIONS = [5, 10, 25];
const WEEKDAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export const REPORT_TOP_DATES_TABLE = "REPORT_TOP_DATES_TABLE";
const ReportTopDatesTable = ({
    data = {},
    title,
    dateType = DATATYPE_RESDATE,
    defaultTop = 10,
    place = "default",
    metrics = ["bookingsTotal", "roomNightsTotal", "revenueTotal"],
    defaultMetricSort = "bookingsTotal",
    dayInitials = true,
    description,
    metricsInfo = DISPLAY_ELEMENT_HELPER,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const [top, setTop] = useState(
        TOP_OPTIONS.includes(defaultTop) ? defaultTop : 10,
    );
    const [sortMetric, setSortMetric] = useState(defaultMetricSort);

    useEffect(() => {
        setTop(TOP_OPTIONS.includes(defaultTop) ? defaultTop : 10);
    }, [defaultTop]);

    useEffect(() => {
        if (
            !metrics.includes(sortMetric) &&
            sortMetric !== "date" &&
            sortMetric !== "day"
        ) {
            setSortMetric(defaultMetricSort);
        }
    }, [metrics, sortMetric]);

    const rows = useMemo(() => {
        const grouped = {};

        Object.values(data ?? {}).forEach(({ keys, result }) => {
            const k = Object.fromEntries(keys ?? []);

            const year = Number(
                k[GROUP_DATE_YEAR] ?? new Date().getUTCFullYear(),
            );
            const month = Number(k[GROUP_DATE_MONTH] ?? 1);
            const day = Number(k[GROUP_DATE_DAY] ?? 1);
            const date = new Date(Date.UTC(year, month - 1, day));
            const key = `${year}-${String(month).padStart(2, "0")}-${String(
                day,
            ).padStart(2, "0")}`;

            const weekdayIndex = (date.getUTCDay() + 6) % 7;
            grouped[key] = grouped[key] ?? {
                date: key,
                day: WEEKDAYS[weekdayIndex],
                sortDay: weekdayIndex,
                sortDate: year * 10000 + month * 100 + day,
            };

            metrics.forEach((metric) => {
                grouped[key][metric] =
                    (grouped[key][metric] ?? 0) +
                    (Number(result?.[metric]) || 0);
            });
        });

        return Object.values(grouped)
            .sort((a, b) =>
                sortMetric === "date"
                    ? a.sortDate - b.sortDate
                    : sortMetric === "day"
                      ? a.sortDay - b.sortDay
                      : (Number(b[sortMetric]) || 0) -
                        (Number(a[sortMetric]) || 0),
            )
            .slice(0, top);
    }, [data, metrics, sortMetric, top]);

    const tableTitle =
        title ??
        (dateType === DATATYPE_RESDATE
            ? "Top booked dates"
            : "Top selling dates");

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pt: 2 }}
            >
                <InfoIcon title={title} description={description} />

                <FormControl size="small" sx={{ mb: 1 }}>
                    <Select
                        value={top}
                        onChange={(e) => setTop(e.target.value)}
                    >
                        {TOP_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                Top {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TableSortLabel
                                        active={sortMetric === "date"}
                                        direction="asc"
                                        onClick={() => setSortMetric("date")}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TableSortLabel
                                        active={sortMetric === "day"}
                                        direction="asc"
                                        onClick={() => setSortMetric("day")}
                                    >
                                        Day
                                    </TableSortLabel>
                                </TableCell>

                                {metrics.map((metric) => (
                                    <TableCell
                                        key={metric}
                                        align="right"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        <TableSortLabel
                                            active={sortMetric === metric}
                                            direction="desc"
                                            onClick={() =>
                                                setSortMetric(metric)
                                            }
                                        >
                                            {metricsInfo[metric]
                                                ?.abbreviation ??
                                                getMetricName(metric, place) ??
                                                metric}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.length > 0 ? (
                                rows.map((row) => (
                                    <TableRow key={row.date}>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell>
                                            {dayInitials
                                                ? row.day.substring(0, 3)
                                                : row.day}
                                        </TableCell>

                                        {metrics.map((metric) => (
                                            <TableCell
                                                key={metric}
                                                align="right"
                                            >
                                                {fns(
                                                    hotelID,
                                                    row[metric] ?? 0,
                                                    metricsInfo[metric]
                                                        ?.format ?? {},
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <NoDataRow text="No data for these filters" />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default ReportTopDatesTable;
